import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Image as ImageIcon, Sun, Moon, Plus, LayoutDashboard, Handshake,
  FolderKanban, Users, MoreHorizontal,
} from "lucide-react";
import { hexToChannels } from "../context/BrandingContext";
import { formatMoney } from "../format";

/**
 * A miniature mock of the app, rendered with a branding record that has not
 * been saved yet -- so an admin can see a logo, colour or wordmark in place
 * before committing it, and (in the discovery flow) judge a scraped brand
 * against their own UI instead of against a hex string in a table.
 *
 * ## How it repaints without touching the surrounding page
 *
 * Both the brand colours and the five theme tokens are CSS variables, and the
 * light/dark theme is a plain var-swap between `:root` and `.dark` in
 * index.css -- *not* a `dark:` variant. Every compiled rule is therefore of the
 * form `background-color: var(--surface)` or
 * `background-color: rgb(var(--brand-primary) / <alpha>)`, which resolves at
 * computed-value time per element. Redefining those variables inline on one
 * wrapper `<div>` gives that subtree its own brand *and* its own theme by
 * ordinary inheritance, with no new machinery and no effect on the page around
 * it. That is also why the preview can show light while the app is in dark
 * mode: nothing here depends on an ancestor `.dark` selector.
 *
 * ### Discipline rule for everything inside this file
 *
 * **Token classes only** -- `bg-bg`, `bg-surface`, `bg-frost`, `text-txt`,
 * `text-muted`, `border-border`, `bg-primary`, `text-glow`. Never a `dark:`
 * variant and never a literal palette colour (`bg-white`, `text-gray-500`):
 * those follow the *app's* real theme and would silently break the preview's
 * independence, which is the one property this component exists for. The two
 * deliberate exceptions are the login panel's `#060B19` scrim and the phone
 * bezel, which are fixed in the real UI too.
 *
 * ## Why this is a mockup and not the real Layout
 *
 * Reusing `Layout`/`Sidebar`/`Login` would be more faithful for about a week.
 * They are wired into the router, `useAuth`, `NotificationContext` and i18n, so
 * rendering them here means mocking four providers to draw a thumbnail, and
 * every future change to real navigation would break the Settings page. The
 * drift is accepted: nobody picks a brand colour based on whether the nav
 * padding is exact. Structure and class names below mirror Layout.jsx and
 * Login.jsx closely enough to stay recognisable.
 */

// Mirrors index.css's `:root` and `.dark` blocks. Duplicated rather than read
// from the stylesheet because the preview has to render a theme the document is
// *not* currently in, so `getComputedStyle` on the real root is the wrong
// source. Keep in sync with index.css if the palette ever changes.
const THEMES = {
  light: {
    "--bg": "#ffffff",
    "--surface": "#f9fafb",
    "--border": "#e5e7eb",
    "--txt": "#030712",
    "--muted": "#4b5563",
    "--frost": "rgb(255 255 255 / 0.82)",
    "--surface-soft": "rgb(249 250 251 / 0.5)",
  },
  dark: {
    "--bg": "#060b19",
    "--surface": "#111827",
    "--border": "#1f2937",
    "--txt": "#f9fafb",
    "--muted": "#9ca3af",
    "--frost": "rgb(6 11 25 / 0.82)",
    "--surface-soft": "rgb(17 24 39 / 0.5)",
  },
};

const SURFACES = [
  { key: "app", labelKey: "branding.previewApp" },
  { key: "login", labelKey: "branding.previewLogin" },
  { key: "mobile", labelKey: "branding.previewMobile" },
];

const NAV = [
  { key: "dashboard", icon: LayoutDashboard },
  { key: "deals", icon: Handshake },
  { key: "projects", icon: FolderKanban },
  { key: "contacts", icon: Users },
];

const MOBILE_NAV = [LayoutDashboard, Handshake, FolderKanban, Users, MoreHorizontal];

// One representative figure, so the money formatting (and with it the
// instance's default currency) is visible rather than implied.
const DEMO_AMOUNT = 128400;

/** The ten variables that make a subtree render as a different instance. A
 *  malformed hex -- which happens on every keystroke while someone types one
 *  into the box -- is omitted rather than written, so the value inherited from
 *  `:root` shows through instead of the colour going invalid. Same tolerance
 *  BrandingContext applies to the live record. */
function cssVars(record, theme) {
  const vars = { ...THEMES[theme] };
  const brand = {
    "--brand-primary": record.primary,
    "--brand-primary-hover": record.primary_hover,
    "--brand-glow": record.glow,
  };
  for (const [name, hex] of Object.entries(brand)) {
    const channels = hexToChannels(hex);
    if (channels) vars[name] = channels;
  }
  return vars;
}

/** An image that degrades to a neutral tile. A blank slot and a broken URL are
 *  both ordinary states here -- the record is mid-edit, and a discovery
 *  proposal points at a third-party URL that may well 404 -- so neither may
 *  render as a broken-image box. */
function PreviewImg({ src, alt = "", className = "", iconSize = 12, fit = "contain" }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  if (!src || failed) {
    return (
      <span className={`inline-flex items-center justify-center bg-surface border border-border rounded-sm ${className}`}>
        <ImageIcon size={iconSize} className="text-muted" />
      </span>
    );
  }
  return (
    <img src={src} alt={alt} onError={() => setFailed(true)}
      className={`${fit === "cover" ? "object-cover" : "object-contain"} ${className}`} />
  );
}

/** The wordmark's two-piece shape, local rather than the shared <Wordmark />:
 *  that one reads the saved record out of context, and the whole point here is
 *  to render a record that is not saved. */
function PreviewWordmark({ record, className = "", accentClassName = "text-primary" }) {
  return (
    <div className={className}>
      {record.wordmark_text}
      {record.wordmark_accent ? <span className={accentClassName}>{record.wordmark_accent}</span> : null}
    </div>
  );
}

/** Stands in for the chart pages, whose palette is led by the two brand
 *  colours. `style` rather than the SVG `stroke` presentation attribute: only
 *  the style property resolves `var()`, which is the same distinction that
 *  makes useChartPalette() hand Recharts raw hex. */
function Sparkline() {
  return (
    <svg viewBox="0 0 72 30" className="w-full h-8 mt-1" preserveAspectRatio="none" aria-hidden="true">
      <polyline points="0,22 14,15 29,18 43,9 58,12 72,3" fill="none" strokeWidth="1.5"
        vectorEffect="non-scaling-stroke" style={{ stroke: "rgb(var(--brand-primary))" }} />
      <polyline points="0,27 14,25 29,20 43,23 58,16 72,18" fill="none" strokeWidth="1.5"
        vectorEffect="non-scaling-stroke" style={{ stroke: "rgb(var(--brand-glow))" }} />
    </svg>
  );
}

/** A fake browser tab. The only way to preview `favicon_url` and
 *  `document_title` at all -- both are otherwise invisible until an admin
 *  saves and reloads the page. */
function TabStrip({ record }) {
  return (
    <div className="flex items-end gap-1 px-2 pt-1.5 bg-surface border-b border-border">
      <div className="flex items-center gap-1.5 min-w-0 max-w-[75%] px-2 py-1 rounded-t-sm bg-bg border border-border border-b-0">
        <PreviewImg src={record.favicon_url} className="w-3 h-3 shrink-0" iconSize={8} />
        <span className="text-[9px] text-txt truncate">{record.document_title}</span>
      </div>
    </div>
  );
}

function AppSurface({ record, t }) {
  const tagline = record.tagline || t("app.tagline");
  return (
    <div className="flex h-[236px] bg-bg text-txt">
      <aside className="w-[104px] shrink-0 border-r border-border flex flex-col">
        <div className="px-2 py-2 border-b border-border">
          <div className="flex items-center gap-1.5">
            <PreviewImg src={record.logo_icon_url} className="w-4 h-4 shrink-0" iconSize={9} />
            <PreviewWordmark record={record}
              className="font-display text-[13px] font-black tracking-tighter truncate" />
          </div>
          <div className="text-[6px] uppercase tracking-[0.18em] text-muted mt-1 truncate">{tagline}</div>
        </div>
        <nav className="p-1.5 space-y-0.5">
          {NAV.map(({ key, icon: Icon }, i) => (
            <div key={key}
              className={`flex items-center gap-1.5 px-1.5 py-1 rounded-sm text-[8px] ${
                i === 0 ? "bg-primary text-white" : "text-muted"}`}>
              <Icon size={9} className="shrink-0" />
              <span className="truncate">{t(`nav.${key}`)}</span>
            </div>
          ))}
        </nav>
      </aside>

      <main className="flex-1 min-w-0 p-2.5 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="font-display text-[11px] font-bold truncate">{t("nav.dashboard")}</div>
          {/* Real hover, so primary_hover is checkable and not just a swatch. */}
          <div className="inline-flex items-center gap-1 shrink-0 px-1.5 py-1 rounded-sm text-[8px] text-white
                          bg-primary hover:bg-primary-hover transition-colors cursor-default">
            <Plus size={8} /> {t("common.new")}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="border border-border rounded-sm bg-surface p-2 min-w-0">
            <div className="text-[6px] uppercase tracking-[0.18em] text-muted truncate">
              {t("dashboard.pipelineValue")}
            </div>
            <div className="font-display text-[13px] font-bold mt-0.5 tabular-nums truncate">
              {formatMoney(DEMO_AMOUNT, record.default_currency)}
            </div>
          </div>
          <div className="border border-border rounded-sm bg-surface p-2 min-w-0">
            <div className="text-[6px] uppercase tracking-[0.18em] text-muted truncate">
              {t("dashboard.pipelineByStage")}
            </div>
            <Sparkline />
          </div>
        </div>

        <div className="border border-border rounded-sm bg-surface p-2 space-y-1.5">
          {[["primary", 68], ["glow", 44], ["primary", 56]].map(([tone, width], i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: `rgb(var(--brand-${tone}))` }} />
              <span className="h-1 rounded-full bg-border" style={{ width: `${width}%` }} />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

function LoginSurface({ record, t }) {
  return (
    <div className="flex h-[236px] bg-bg text-txt">
      {/* Mirrors Login.jsx: a configured image, otherwise a gradient drawn from
          the instance's own brand colours so a fresh install has no external
          image dependency on its login screen. */}
      <div className="w-1/2 relative overflow-hidden border-r border-border bg-[#060B19]">
        {record.login_image_url ? (
          <PreviewImg src={record.login_image_url} fit="cover"
            className="absolute inset-0 w-full h-full opacity-60" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-primary/10 to-glow/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#060B19] via-[#060B19]/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-3">
          <div className="flex items-center gap-1.5 min-w-0">
            <PreviewImg src={record.logo_url} className="w-6 h-6 shrink-0" iconSize={12} />
            <PreviewWordmark record={record} accentClassName="text-glow"
              className="font-display text-lg font-black tracking-tighter text-white truncate" />
          </div>
          <p className="text-white/70 text-[7px] leading-snug mt-1.5">{t("auth.pitch")}</p>
        </div>
      </div>

      <div className="flex-1 min-w-0 flex items-center p-3">
        <div className="w-full space-y-1">
          <div className="font-display text-[13px] font-bold">{t("auth.welcome")}</div>
          {/* org_name reaches the login copy through interpolation, exactly as
              the real page does -- so renaming the org is visible here too. */}
          <div className="text-[7px] text-muted truncate">
            {t("auth.subtitle", { brand: record.org_name })}
          </div>
          <div className="space-y-1.5 pt-2">
            <div className="h-4 rounded-sm border border-border bg-surface" />
            <div className="h-4 rounded-sm border border-border bg-surface" />
            <div className="h-4 rounded-sm bg-primary" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileSurface({ record, t }) {
  return (
    <div className="h-[236px] bg-surface-soft flex items-center justify-center">
      {/* The phone frame is what makes theme_color previewable: it is the
          mobile browser's own chrome tint and appears nowhere inside the app. */}
      <div className="w-[126px] h-[212px] rounded-[13px] border-[3px] border-[#1f2937] bg-bg text-txt
                      overflow-hidden flex flex-col shadow-lg">
        <div className="h-3.5 shrink-0 flex items-center justify-between px-1.5 text-[5px] text-white/80"
          style={{ background: record.theme_color }}>
          <span>9:41</span>
          <span aria-hidden="true">▮▮▮</span>
        </div>
        <div className="px-1.5 py-1 border-b border-border bg-frost flex items-center gap-1 min-w-0">
          <PreviewImg src={record.logo_icon_url} className="w-3 h-3 shrink-0" iconSize={7} />
          <PreviewWordmark record={record}
            className="font-display text-[10px] font-black tracking-tighter truncate" />
        </div>
        <div className="flex-1 min-h-0 p-1.5 space-y-1.5">
          <div className="border border-border rounded-sm bg-surface p-1.5 min-w-0">
            <div className="text-[5px] uppercase tracking-[0.15em] text-muted truncate">
              {t("dashboard.pipelineValue")}
            </div>
            <div className="font-display text-[10px] font-bold tabular-nums truncate">
              {formatMoney(DEMO_AMOUNT, record.default_currency)}
            </div>
          </div>
          <div className="rounded-sm bg-primary h-4" />
          <div className="border border-border rounded-sm bg-surface h-4" />
        </div>
        <div className="h-6 shrink-0 border-t border-border bg-frost grid grid-cols-5">
          {MOBILE_NAV.map((Icon, i) => (
            <div key={i} className={`flex items-center justify-center ${i === 0 ? "text-primary" : "text-muted"}`}>
              <Icon size={10} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * @param {object} record  A complete branding record -- the Settings form's
 *   live state, or that state with a discovery proposal's accepted fields
 *   merged over it. One prop on purpose: it is what lets both callers share
 *   this component.
 */
export default function BrandPreview({ record, className = "" }) {
  const { t } = useTranslation();
  const [surface, setSurface] = useState("app");
  // Independent of the app's own theme, on purpose: a dark navy primary can
  // look sharp on white and vanish against the dark ground, and without this
  // toggle half of every colour decision goes unverified until someone
  // complains.
  const [theme, setTheme] = useState("light");

  if (!record) return null;
  const Surface = { app: AppSurface, login: LoginSurface, mobile: MobileSurface }[surface];

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <p className="text-xs uppercase tracking-[0.2em] text-muted">{t("branding.preview")}</p>
        <div className="flex items-center gap-1">
          <div className="flex items-center rounded-sm border border-border overflow-hidden">
            {SURFACES.map(({ key, labelKey }) => (
              <button key={key} type="button" onClick={() => setSurface(key)}
                data-testid={`brand-preview-${key}`}
                aria-pressed={surface === key}
                className={`px-2 py-1 text-xs transition-colors ${
                  surface === key ? "bg-primary text-white" : "text-muted hover:bg-surface hover:text-txt"}`}>
                {t(labelKey)}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setTheme((v) => (v === "light" ? "dark" : "light"))}
            data-testid="brand-preview-theme"
            title={t(theme === "light" ? "branding.previewDark" : "branding.previewLight")}
            aria-label={t(theme === "light" ? "branding.previewDark" : "branding.previewLight")}
            className="p-1.5 rounded-sm border border-border text-muted hover:bg-surface hover:text-txt transition-colors">
            {theme === "light" ? <Moon size={13} /> : <Sun size={13} />}
          </button>
        </div>
      </div>

      {/* The scoped-variable wrapper. Everything below renders as the instance
          being previewed, in the theme chosen above. */}
      <div style={cssVars(record, theme)}
        data-testid="brand-preview-frame"
        className="rounded-sm border border-border overflow-hidden select-none">
        {surface !== "mobile" && <TabStrip record={record} />}
        <Surface record={record} t={t} />
      </div>

      <p className="text-xs text-muted mt-2">{t("branding.previewHint")}</p>
    </div>
  );
}
